/**
 * qkey.co marketing nav — 2026 redesign.
 *
 * PORTED FROM QSAVE (staking platform, src/lib/site/SiteNav.svelte). The
 * geometry is carried over exactly: 56px tall, sticky, ground at 86% + 10px
 * blur, hairline bottom rule, IBM Plex Mono throughout, 13/14px type,
 * 0.05em/0.06em tracking, 3px radii. The 56px height is COUPLED to the
 * scroll-margin-top:76px anchor offset in tokens.css — change one and the
 * other must follow, or every anchor jump tucks its heading under the nav.
 *
 * BEHAVIOUR IS PORTED TOO, not re-invented. Menus open on CLICK, not hover
 * (hover is unusable on touch); one open at a time; Escape closes and returns
 * focus to the trigger; outside click closes; navigation closes. Those are
 * accessibility requirements, not stylistic choices, which is why they survive
 * the Svelte → React translation unchanged.
 *
 * WHAT DIFFERS FROM QSAVE — and only this:
 *   · the mark and wordmark are QKey's (see the lockup note below);
 *   · the links are QKey's (links.ts);
 *   · the accent is indigo, via the qk-* tokens;
 *   · the wordmark is SANS. QSAVE sets its wordmark in Spectral to match its
 *     serif H1. QKey has no serif, so the wordmark is Plex Sans at the same
 *     22px/medium/0.02em the QSAVE lockup uses. Tracking stays near-zero for
 *     the same reason it does there — the 0.14em that suits 13px mono caps
 *     pulls a 22px wordmark apart.
 *   · there is no EN locale chip and no GEO dev pill. Both are QSAVE platform
 *     features (i18n, jurisdiction gating) with no counterpart on qkey.co;
 *     porting empty shells of them would be cargo-culting.
 *
 * The 901px collapse breakpoint is QSAVE's and is load-bearing in TWO places —
 * the `min-[901px]:` classes and the matchMedia below. Keep them in step.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useThemeStore } from '@/lib/store/theme'
import { NAV, CTA, SECONDARY_CTA, type NavEntry, type SiteLink } from './links'
import { QkeyMark } from './QkeyLogo'

const TRIGGER =
  'flex items-center gap-1.5 text-qk-dim no-underline transition-colors hover:text-qk-accent'
const ITEM =
  'block whitespace-nowrap px-4 py-2.5 text-qk-dim no-underline transition-colors hover:bg-qk-hover hover:text-qk-accent'

/** Internal routes go through the router; external ones are plain anchors. */
function SmartLink({ link, className, role }: { link: SiteLink; className: string; role?: string }) {
  if (link.external) {
    return (
      <a href={link.href} target="_blank" rel="noopener noreferrer" className={className} role={role}>
        {link.label}
      </a>
    )
  }
  return (
    <Link to={link.href} className={className} role={role}>
      {link.label}
    </Link>
  )
}

export function QkeySiteNav() {
  const { theme, toggleTheme } = useThemeStore()
  const location = useLocation()

  /** Label of the open menu, or null. Only one may be open at a time. */
  const [openKey, setOpenKey] = useState<string | null>(null)
  const [mobileOpen, setMobileOpen] = useState(false)

  const navEl = useRef<HTMLElement | null>(null)
  const triggers = useRef<Record<string, HTMLButtonElement | null>>({})
  const burgerEl = useRef<HTMLButtonElement | null>(null)

  /** Close and hand focus back to the trigger — required for keyboard users,
      who would otherwise be dropped at the top of the document. */
  const closeTo = useCallback((key: string) => {
    setOpenKey(null)
    triggers.current[key]?.focus()
  }, [])

  // Escape closes the menu, then the mobile panel — innermost first.
  useEffect(() => {
    const onKeydown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (openKey) {
        event.stopPropagation()
        closeTo(openKey)
      } else if (mobileOpen) {
        event.stopPropagation()
        setMobileOpen(false)
        burgerEl.current?.focus()
      }
    }
    window.addEventListener('keydown', onKeydown)
    return () => window.removeEventListener('keydown', onKeydown)
  }, [openKey, mobileOpen, closeTo])

  // Outside click closes both.
  useEffect(() => {
    const onPointer = (event: MouseEvent) => {
      if (navEl.current && !navEl.current.contains(event.target as Node)) {
        setOpenKey(null)
        setMobileOpen(false)
      }
    }
    document.addEventListener('click', onPointer)
    return () => document.removeEventListener('click', onPointer)
  }, [])

  // Close the panel whenever the viewport crosses the collapse breakpoint.
  // Without this, a panel left open before widening stays `true` while CSS
  // hides it — and pops straight back open the moment the window narrows.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 901px)')
    const onChange = () => setMobileOpen(false)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  // A menu left open across a navigation would hang over the new page.
  useEffect(() => {
    setOpenKey(null)
    setMobileOpen(false)
  }, [location.pathname])

  return (
    <nav
      ref={navEl}
      data-qk-nav
      className="sticky top-0 z-[8] flex h-14 items-center justify-between gap-8 border-b
                 border-qk-rule bg-qk-bg/[0.86] px-10 font-plex backdrop-blur-[10px]"
    >
      {/* LOCKUP. Mirrors QSAVE's exactly — a 34px rounded mark, a 10px gap, and
          the wordmark. The mark is favicon.png, which IS the QKey icon badge
          (QKEY_BRAND_GUIDELINES.md §2a: circular indigo→violet gradient, white
          Q with a key tail). It is used rather than qkey-padlock.png because
          the padlock asset is a glow render baked onto a BLACK field — it sits
          invisibly on the near-black dark ground and as a black tile on paper.
          A self-contained badge works on both, which is what a nav mark has to
          do here. `rounded-full` rather than QSAVE's rounded-[7px]: the QKey
          badge is a circle, QSAVE's is a rounded square. */}
      <Link to="/" className="flex shrink-0 items-center gap-2.5 no-underline" aria-label="QKey home">
        <QkeyMark size={34} className="h-[34px] w-[34px]" />
        <span className="font-plexsans text-[22px] font-medium tracking-[0.02em] text-qk-ink">
          QKEY
        </span>
      </Link>

      <div
        data-qk-navlinks
        className="hidden items-center gap-[26px] text-[14px] tracking-[0.05em] min-[901px]:flex"
      >
        {NAV.map((entry: NavEntry) =>
          entry.href ? (
            entry.href.startsWith('http') ? (
              <a
                key={entry.label}
                href={entry.href}
                target="_blank"
                rel="noopener noreferrer"
                className={TRIGGER}
              >
                {entry.label}
              </a>
            ) : (
              <Link key={entry.label} to={entry.href} className={TRIGGER}>
                {entry.label}
              </Link>
            )
          ) : (
            <div key={entry.label} className="relative">
              <button
                type="button"
                ref={(el) => {
                  triggers.current[entry.label] = el
                }}
                className={TRIGGER}
                aria-haspopup="true"
                aria-expanded={openKey === entry.label}
                onClick={(e) => {
                  e.stopPropagation()
                  setOpenKey((k) => (k === entry.label ? null : entry.label))
                }}
              >
                {entry.label}
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  className={`h-3 w-3 transition-transform duration-200 ${
                    openKey === entry.label ? 'rotate-180' : ''
                  }`}
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              {openKey === entry.label && (
                <div
                  role="menu"
                  aria-label={entry.label}
                  className="absolute left-0 top-[calc(100%+10px)] min-w-[232px] rounded-[3px] border
                             border-qk-rule-strong bg-qk-raised py-1.5 text-[11.5px]
                             shadow-[var(--qk-pop-shadow)]"
                >
                  {(entry.items ?? []).map((item) => (
                    <SmartLink key={item.href} link={item} className={ITEM} role="menuitem" />
                  ))}
                </div>
              )}
            </div>
          )
        )}
      </div>

      <div className="flex items-center gap-2.5 text-[13px] tracking-[0.06em]">
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          className="flex items-center rounded-[3px] border border-qk-rule-strong px-[9px] py-[5px]
                     text-qk-dim transition-colors hover:text-qk-accent"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="h-[13px] w-[13px]"
          >
            {theme === 'dark' ? (
              /* Sun: offers the light theme */
              <>
                <circle cx="12" cy="12" r="4" />
                <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
              </>
            ) : (
              /* Moon: offers the dark theme */
              <path d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z" />
            )}
          </svg>
        </button>

        <Link
          to={CTA.href}
          className="rounded-[3px] bg-qk-accent px-3 py-1.5 font-medium text-qk-on-accent no-underline
                     transition-colors hover:bg-qk-accent-hi"
        >
          {CTA.label}
        </Link>
        <Link
          to={SECONDARY_CTA.href}
          className="hidden text-qk-dim no-underline transition-colors hover:text-qk-accent min-[901px]:block"
        >
          {SECONDARY_CTA.label}
        </Link>

        <button
          type="button"
          ref={burgerEl}
          onClick={(e) => {
            e.stopPropagation()
            setMobileOpen((o) => !o)
          }}
          aria-expanded={mobileOpen}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          className="flex items-center rounded-[3px] border border-qk-rule-strong px-[9px] py-[5px]
                     text-qk-dim transition-colors hover:text-qk-accent min-[901px]:hidden"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
            className="h-[15px] w-[15px]"
          >
            {mobileOpen ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M3 6h18M3 12h18M3 18h18" />}
          </svg>
        </button>
      </div>

      {mobileOpen && (
        /* The collapsed-nav panel. Lives INSIDE <nav> so the outside-click
           handler's contains() check covers it. Dropdown groups flatten to a
           labelled section; the CTAs stay in the bar above. */
        <div
          className="absolute inset-x-0 top-full max-h-[calc(100vh-56px)] overflow-y-auto border-b
                     border-qk-rule-strong bg-qk-bg/[0.97] px-7 py-3 text-[14px] tracking-[0.05em]
                     shadow-[var(--qk-pop-shadow)] backdrop-blur-[10px] min-[901px]:hidden"
        >
          {NAV.map((entry) =>
            entry.href ? (
              <SmartLink
                key={entry.label}
                link={{ label: entry.label, href: entry.href, external: entry.href.startsWith('http') }}
                className="block py-2.5 text-qk-dim no-underline transition-colors hover:text-qk-accent"
              />
            ) : (
              <div
                key={entry.label}
                className="mt-1.5 border-t border-qk-rule pt-1.5 first:mt-0 first:border-t-0 first:pt-0"
              >
                <div className="py-1.5 text-[10.5px] uppercase tracking-[0.17em] text-qk-faint">
                  {entry.label}
                </div>
                {(entry.items ?? []).map((item) => (
                  <SmartLink
                    key={item.href}
                    link={item}
                    className="block py-2.5 pl-3 text-qk-dim no-underline transition-colors hover:text-qk-accent"
                  />
                ))}
              </div>
            )
          )}
          {/* The secondary CTA is hidden in the bar below 901px (it would
              crowd the burger), so the panel is where it lives instead. */}
          <div className="mt-1.5 border-t border-qk-rule pt-1.5">
            <SmartLink
              link={SECONDARY_CTA}
              className="block py-2.5 text-qk-dim no-underline transition-colors hover:text-qk-accent"
            />
          </div>
        </div>
      )}
    </nav>
  )
}
